import { callable } from '../callable';

export type AssignmentType =
  | 'reading-review'
  | 'essay'
  | 'quiz'
  | 'project'
  | 'attendance'
  | 'socratic'
  | 'other';
export type SubmissionType = 'file' | 'text' | 'url';
export type SubmissionStatus = 'draft' | 'submitted' | 'graded' | 'returned';

export interface SubmissionFileDto {
  storagePath: string;
  originalFileName: string;
  mimeType?: string;
  sizeBytes?: number;
  uploadedAt: string;
}

export interface RubricAssessmentEntryDto {
  criterionId: string;
  score: number;
  comment?: string;
}

/** Scannable three-part formative summary shown to the student. */
export interface FormativeBreakdownDto {
  achieved: string;
  missing: string;
  nextStep: string;
}

export interface SubmissionGradeDto {
  rawPoints: number;
  latePenalty: number;
  finalPoints: number;
  rubricAssessment?: RubricAssessmentEntryDto[];
  feedback?: string;
  formativeBreakdown?: FormativeBreakdownDto;
  gradedBy: string;
  gradedAt: string;
}

export interface AssignmentSummaryDto {
  id: string;
  sectionId: string;
  type: AssignmentType;
  name: string;
  /** Instructions (TipTap JSON or plain text). */
  description?: Record<string, unknown> | string;
  dueAt?: string;
  /** When set and in the past, the assignment is closed (no more submissions). */
  lockAt?: string;
  weight: number;
  pointsPossible: number;
  submissionTypes: SubmissionType[];
  publishedAt?: string;
  /** CAP (Phase E): signature-assessment mark + the PLO it evaluates. */
  isSignatureAssignment?: boolean;
  evaluatesOutcomeId?: string;
  submissionCount: number;
  gradedCount: number;
  studentSubmission?: {
    id: string;
    status: SubmissionStatus;
    submittedAt?: string;
    finalPoints?: number;
    latePenalty?: number;
  };
}

export interface GradingRosterRowDto {
  /** Vacío cuando el alumno NO entregó: no hay documento todavía. */
  id: string;
  studentId: string;
  studentName: string;
  status: string;
  submittedAt?: string;
  graded?: boolean;
  noDelivery?: boolean;
  draftStarted?: boolean;
  hasWork?: boolean;
}

export interface GradingRosterDto {
  assignmentId: string;
  sectionId: string;
  rows: GradingRosterRowDto[];
}

export interface PdfAnnotationAreaDto {
  pageIndex: number;
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface PdfAnnotationDto {
  id: string;
  fileStoragePath: string;
  quote: string;
  note: string;
  color?: string;
  areas: PdfAnnotationAreaDto[];
  authorId: string;
  createdAt: string;
  /** Provenance: manual selection or an accepted AI-detected issue. */
  source?: 'manual' | 'ai-spelling' | 'ai-format';
  /** The AI's proposed correction (kept when accepted from an issue). */
  suggestion?: string;
  /** For AI-accepted marks: originating issue key (`${page}|${quote}`). */
  issueKey?: string;
}

export interface SubmissionDto {
  id: string;
  assignmentId: string;
  sectionId: string;
  studentId: string;
  status: SubmissionStatus;
  attempt: number;
  submittedAt?: string;
  files: SubmissionFileDto[];
  textBody?: string;
  url?: string;
  grade?: SubmissionGradeDto;
  returnNote?: string;
  revisionHistory?: SubmissionRevisionEventDto[];
  /** Teacher PDF marks. Only present for the student once graded/returned. */
  annotations?: PdfAnnotationDto[];
  /** AI-issue keys the teacher dismissed (staff-only; never sent to students). */
  dismissedIssueKeys?: string[];
  /** Nota puesta SIN que el alumno entregara. Mientras la tarea siga abierta el
   *  alumno todavía puede entregar, y su trabajo reemplaza esa nota. */
  absent?: boolean;
}

export interface SubmissionRevisionEventDto {
  note: string;
  at: string;
  by: string;
  attempt: number;
}

// ─── Assisted Review (AI) ───────────────────────────────────────────────────

export type AssistedReviewStatus = 'pending' | 'ready' | 'failed' | 'applied' | 'discarded';

export interface AssistedReviewCriterionDto {
  criterionId: string;
  criterionLabel?: string;
  suggestedLevel?: string;
  suggestedScore: number;
  evidence: string[];
  gap: string;
  confidence: 'low' | 'medium' | 'high';
}

export interface AssistedReviewFlagsDto {
  offTopic?: boolean;
  missingSections?: boolean;
  possibleIntegrity?: boolean;
  unsupportedContent?: boolean;
}

export interface AssistedReviewIssueDto {
  page: number;
  quote: string;
  context?: string;
  type: 'spelling' | 'format';
  severity?: 'low' | 'medium' | 'high';
  suggestion: string;
}

export interface AssistedReviewDto {
  id: string;
  submissionId: string;
  assignmentId: string;
  sectionId: string;
  attempt: number;
  status: AssistedReviewStatus;
  model: string;
  promptVersion: string;
  inputDigest: string;
  criteria: AssistedReviewCriterionDto[];
  overallSuggestedPoints: number;
  /** DEPRECATED (v8+): long prose replaced by formativeBreakdown. Optional so
   *  legacy reviews still render. */
  draftFeedbackFormative?: string;
  draftFeedbackFormativeConcise?: string;
  draftFeedbackSummative?: string;
  draftFeedbackSummativeConcise?: string;
  formativeBreakdown?: FormativeBreakdownDto;
  issues?: AssistedReviewIssueDto[];
  flags: AssistedReviewFlagsDto;
  costEstimateUsd?: number;
  error?: string;
  generatedBy: string;
  generatedAt: string;
}

export interface RubricLevelDto {
  name: string;
  description: string;
}

export interface RubricCriterionDto {
  id: string;
  label: string;
  description: string;
  weight: number;
  levels?: RubricLevelDto[];
}

export interface ReviewCriterionConfigDto {
  criterionId: string;
  enabled: boolean;
  note?: string;
}

export interface ReviewConfigDto {
  criteria: ReviewCriterionConfigDto[];
  instructions?: string;
  updatedAt?: string;
  updatedBy?: string;
}

// ─── Quiz (Delivery 1: deterministic engine, closed kinds) ──────────────────

export type QuizQuestionKind =
  | 'single_choice'
  | 'multiple_choice'
  | 'matching'
  | 'short_answer'
  | 'ordering'
  | 'numeric'
  | 'cloze'
  | 'open';

export interface QuizOptionDto {
  id: string;
  text: string;
}

export interface ShortAnswerNormalizationDto {
  trim: boolean;
  lowercase: boolean;
  stripAccents: boolean;
}

/** One question as the TEACHER authors it — carries BOTH the public shape and
 *  the answer key. Sent to `createQuiz`; the server splits it. */
export type QuizQuestionInput =
  | {
      id: string;
      kind: 'single_choice';
      prompt: string | Record<string, unknown>;
      points: number;
      options: QuizOptionDto[];
      correctOptionId: string;
    }
  | {
      id: string;
      kind: 'multiple_choice';
      prompt: string | Record<string, unknown>;
      points: number;
      options: QuizOptionDto[];
      correctOptionIds: string[];
      partialCreditPolicy?: 'all-or-nothing' | 'proportional';
    }
  | {
      id: string;
      kind: 'matching';
      prompt: string | Record<string, unknown>;
      points: number;
      left: QuizOptionDto[];
      right: QuizOptionDto[];
      correctPairs: { leftId: string; rightId: string }[];
    }
  | {
      id: string;
      kind: 'short_answer';
      prompt: string | Record<string, unknown>;
      points: number;
      acceptedAnswers: string[];
      normalization?: ShortAnswerNormalizationDto;
    }
  | {
      id: string;
      kind: 'ordering';
      prompt: string | Record<string, unknown>;
      points: number;
      items: QuizOptionDto[];
      correctOrder: string[];
    }
  | {
      id: string;
      kind: 'numeric';
      prompt: string | Record<string, unknown>;
      points: number;
      unit?: string;
      value: number;
      tolerance: number;
    }
  | {
      id: string;
      kind: 'cloze';
      prompt: string | Record<string, unknown>;
      points: number;
      template: string;
      blanks: Record<
        string,
        { acceptedAnswers: string[]; normalization?: ShortAnswerNormalizationDto }
      >;
    }
  | {
      id: string;
      kind: 'open';
      prompt: string | Record<string, unknown>;
      points: number;
      standard?: string;
    };

/** One question as the STUDENT sees it — NEVER contains any answer key. */
export type QuizQuestionPublicDto =
  | {
      id: string;
      kind: 'single_choice';
      prompt: string | Record<string, unknown>;
      points: number;
      options: QuizOptionDto[];
    }
  | {
      id: string;
      kind: 'multiple_choice';
      prompt: string | Record<string, unknown>;
      points: number;
      options: QuizOptionDto[];
    }
  | {
      id: string;
      kind: 'matching';
      prompt: string | Record<string, unknown>;
      points: number;
      left: QuizOptionDto[];
      right: QuizOptionDto[];
    }
  | {
      id: string;
      kind: 'short_answer';
      prompt: string | Record<string, unknown>;
      points: number;
    }
  | {
      id: string;
      kind: 'ordering';
      prompt: string | Record<string, unknown>;
      points: number;
      items: QuizOptionDto[];
    }
  | {
      id: string;
      kind: 'numeric';
      prompt: string | Record<string, unknown>;
      points: number;
      unit?: string;
    }
  | {
      id: string;
      kind: 'cloze';
      prompt: string | Record<string, unknown>;
      points: number;
      template: string;
    }
  | {
      id: string;
      kind: 'open';
      prompt: string | Record<string, unknown>;
      points: number;
      // NO `standard` — the evaluation standard is the teacher's grading guide and
      // never reaches the student. It lives in the answer key (staff-only).
    };

export type QuizAnswerDto =
  | { kind: 'single_choice'; questionId: string; selectedOptionId?: string }
  | { kind: 'multiple_choice'; questionId: string; selectedOptionIds: string[] }
  | { kind: 'matching'; questionId: string; pairs: { leftId: string; rightId: string }[] }
  | { kind: 'short_answer'; questionId: string; text: string }
  | { kind: 'ordering'; questionId: string; orderedItemIds: string[] }
  | { kind: 'numeric'; questionId: string; value?: number }
  | { kind: 'cloze'; questionId: string; values: Record<string, string> }
  | { kind: 'open'; questionId: string; text: string };

export interface QuizPassThresholdDto {
  metric: 'correctCount' | 'percent';
  value: number;
}

export interface QuizScheduleDto {
  publishAt?: string;
  opensAt?: string;
  closesAt?: string;
}

export interface QuizForStudentDto {
  assignmentId: string;
  name: string;
  timeLimitMinutes?: number;
  questions: QuizQuestionPublicDto[];
  dueAt?: string;
  opensAt?: string;
  closesAt?: string;
  open: boolean;
  pointsPossible: number;
  alreadySubmitted: boolean;
}

export interface SubmitQuizResultDto {
  rawPoints: number;
  maxPoints: number;
  latePenalty: number;
  finalPoints: number;
  correctCount: number;
  passed?: boolean;
  perQuestion: { questionId: string; awarded: number; max: number; deferred?: boolean }[];
  /** True when the quiz has open questions: closed part scored, total grade pending
   *  the teacher's review. `finalPoints` is 0 (not yet consolidated) in that case. */
  pendingReview: boolean;
}

export const quizApi = {
  create: async (data: {
    tenantId: string;
    sectionId: string;
    name: string;
    description?: string | Record<string, unknown>;
    weight: number;
    dueAt?: string;
    lockAt?: string;
    gradeComponentId?: string;
    publish?: boolean;
    quiz: {
      timeLimitMinutes?: number;
      passPolicy?: 'sum' | 'threshold';
      passThreshold?: QuizPassThresholdDto;
      schedule?: QuizScheduleDto;
      questions: QuizQuestionInput[];
    };
  }): Promise<{ id: string }> => {
    const fn = callable<typeof data, { id: string }>('createQuiz');
    return (await fn(data)).data;
  },
  getForStudent: async (data: {
    tenantId: string;
    studentId: string;
    assignmentId: string;
  }): Promise<QuizForStudentDto> => {
    const fn = callable<typeof data, QuizForStudentDto>('getQuizForStudent');
    return (await fn(data)).data;
  },
  /** El alumno rinde SU quiz: el servidor lo saca de la sesión. */
  submit: async (data: {
    tenantId: string;
    assignmentId: string;
    answers: QuizAnswerDto[];
  }): Promise<SubmitQuizResultDto> => {
    const fn = callable<typeof data, SubmitQuizResultDto>('submitQuiz');
    return (await fn(data)).data;
  },
  getForEdit: async (data: { tenantId: string; assignmentId: string }): Promise<QuizForEditDto> => {
    const fn = callable<typeof data, QuizForEditDto>('getQuizForEdit');
    return (await fn(data)).data;
  },
  update: async (data: {
    tenantId: string;
    assignmentId: string;
    name: string;
    description?: string | Record<string, unknown>;
    weight: number;
    /** Publish the quiz (finishing a draft). Also clears the saved draft. */
    publish?: boolean;
    quiz: {
      timeLimitMinutes?: number;
      passPolicy?: 'sum' | 'threshold';
      passThreshold?: QuizPassThresholdDto;
      schedule?: QuizScheduleDto;
      questions: QuizQuestionInput[];
    };
  }): Promise<{ id: string }> => {
    const fn = callable<typeof data, { id: string }>('updateQuiz');
    return (await fn(data)).data;
  },
  /** Save an incomplete quiz as an unpublished draft (no validation, no answer key). */
  saveDraft: async (data: {
    tenantId: string;
    sectionId?: string;
    assignmentId?: string;
    name: string;
    weight: number;
    draft: Record<string, unknown>;
  }): Promise<{ id: string }> => {
    const fn = callable<typeof data, { id: string }>('saveQuizDraft');
    return (await fn(data)).data;
  },
};

export interface QuizForEditDto {
  assignmentId: string;
  name: string;
  description: string | Record<string, unknown>;
  weight: number;
  passPolicy?: 'sum' | 'threshold';
  passThreshold?: QuizPassThresholdDto;
  schedule?: QuizScheduleDto;
  questions: QuizQuestionInput[];
  hasSubmissions: boolean;
  /** Whether the quiz is published (drafts are not). */
  published: boolean;
  /** Opaque builder snapshot for an in-progress draft — resume from this when present. */
  draft?: Record<string, unknown>;
}

// ─── Teacher quiz review (staff-only — includes the answer key) ──────────────

export type QuizAnswerKeyEntryDto =
  | { kind: 'single_choice'; correctOptionId: string }
  | { kind: 'multiple_choice'; correctOptionIds: string[] }
  | { kind: 'matching'; correctPairs: { leftId: string; rightId: string }[] }
  | { kind: 'short_answer'; acceptedAnswers: string[]; normalization: ShortAnswerNormalizationDto }
  | { kind: 'ordering'; correctOrder: string[] }
  | { kind: 'numeric'; value: number; tolerance: number }
  | {
      kind: 'cloze';
      blanks: Record<
        string,
        { acceptedAnswers: string[]; normalization: ShortAnswerNormalizationDto }
      >;
    };

export interface QuizOpenAiProposalDto {
  suggestedScore?: number;
  feedback: string;
  confidence: 'low' | 'medium' | 'high';
}

export interface QuizReviewQuestionDto {
  question: QuizQuestionPublicDto;
  key?: QuizAnswerKeyEntryDto;
  /** Evaluation standard (open questions only) — staff-facing. */
  standard?: string;
  studentAnswer?: QuizAnswerDto;
  awarded: number;
  max: number;
  correct: boolean;
  deferred?: boolean;
  aiProposal?: QuizOpenAiProposalDto;
}

export interface QuizReviewDto {
  submissionId: string;
  studentId: string;
  submittedAt?: string;
  rawPoints: number;
  maxPoints: number;
  correctCount: number;
  grade?: { rawPoints: number; finalPoints: number; latePenalty: number };
  questions: QuizReviewQuestionDto[];
  hasOpen: boolean;
  closedRawPoints: number;
  openMaxPoints: number;
  review?: {
    id: string;
    status: 'pending' | 'ready' | 'failed' | 'applied' | 'discarded';
    costEstimateUsd: number;
    generatedAt: string;
  };
}

export const quizReviewApi = {
  get: async (data: {
    tenantId: string;
    assignmentId: string;
    submissionId: string;
  }): Promise<QuizReviewDto> => {
    const fn = callable<typeof data, QuizReviewDto>('getQuizReview');
    return (await fn(data)).data;
  },
};

export const assignmentApi = {
  // Assignments CRUD
  create: async (data: {
    tenantId: string;
    sectionId: string;
    type: AssignmentType;
    name: string;
    description: string | Record<string, unknown>;
    weight: number;
    pointsPossible: number;
    submissionTypes: SubmissionType[];
    dueAt?: string;
    lockAt?: string;
    gradeComponentId?: string;
    /** Reference a rubric from the section's Program catalog by id. */
    rubricRef?: string;
    /** Socratic dialogue config — required when type='socratic'. */
    socraticConfig?: {
      rootQuestion: string;
      standardId: string;
      level: 'review' | 'probe' | 'defense';
      maxAttempts: number;
      audienceLevel?: 'lay' | 'masters' | 'doctorate';
    };
    publish?: boolean;
  }): Promise<{ id: string }> => {
    const fn = callable<typeof data, { id: string }>('createAssignment');
    return (await fn(data)).data;
  },
  update: async (data: {
    tenantId: string;
    assignmentId: string;
    updates: Record<string, unknown>;
  }): Promise<{ success: boolean }> => {
    const fn = callable<typeof data, { success: boolean }>('updateAssignment');
    return (await fn(data)).data;
  },
  publish: async (data: {
    tenantId: string;
    assignmentId: string;
    publish: boolean;
  }): Promise<{ success: boolean }> => {
    const fn = callable<typeof data, { success: boolean }>('publishAssignment');
    return (await fn(data)).data;
  },
  delete: async (data: {
    tenantId: string;
    assignmentId: string;
  }): Promise<{ success: boolean }> => {
    const fn = callable<typeof data, { success: boolean }>('deleteAssignment');
    return (await fn(data)).data;
  },
  list: async (data: {
    tenantId: string;
    sectionId: string;
    requestingStudentId?: string;
  }): Promise<{ items: AssignmentSummaryDto[] }> => {
    const fn = callable<typeof data, { items: AssignmentSummaryDto[] }>('listSectionAssignments');
    return (await fn(data)).data;
  },

  /** La lista de corrección de una tarea: matrícula ∪ entregas, con nombres.
   *  La arma el servidor (PR 3A). */
  listGradingRoster: async (data: {
    tenantId: string;
    assignmentId: string;
  }): Promise<GradingRosterDto> => {
    const fn = callable<typeof data, GradingRosterDto>('listAssignmentGradingRoster');
    return (await fn(data)).data;
  },

  // Submissions
  /** El borrador del alumno de la sesión (el servidor lo resuelve). */
  getOrCreateDraft: async (data: {
    tenantId: string;
    assignmentId: string;
  }): Promise<{ id: string; newlyCreated: boolean }> => {
    const fn = callable<typeof data, { id: string; newlyCreated: boolean }>(
      'getOrCreateDraftSubmission',
    );
    return (await fn(data)).data;
  },
  updateDraft: async (data: {
    tenantId: string;
    submissionId: string;
    files?: SubmissionFileDto[];
    textBody?: string;
    url?: string;
  }): Promise<{ success: boolean }> => {
    const fn = callable<typeof data, { success: boolean }>('updateSubmissionDraft');
    return (await fn(data)).data;
  },
  submit: async (data: {
    tenantId: string;
    submissionId: string;
  }): Promise<{ submittedAt: string }> => {
    const fn = callable<typeof data, { submittedAt: string }>('submitSubmission');
    return (await fn(data)).data;
  },
  /** Nota para un alumno que NO entregó. Camino propio: el de las entregas
   *  exige un documento en estado `submitted`. */
  gradeAbsent: async (data: {
    tenantId: string;
    assignmentId: string;
    studentId: string;
    rawPoints: number;
    feedback?: string;
  }): Promise<{ submissionId: string; finalPoints: number }> => {
    const fn = callable<typeof data, { submissionId: string; finalPoints: number }>(
      'gradeAbsentStudent',
    );
    return (await fn(data)).data;
  },
  /** Cierra de una vez la nota de TODOS los que no entregaron esta tarea. */
  gradeAllAbsent: async (data: {
    tenantId: string;
    assignmentId: string;
    rawPoints: number;
  }): Promise<{ graded: number; keptTheirGrade: number; hasWork: number }> => {
    const fn = callable<typeof data, { graded: number; keptTheirGrade: number; hasWork: number }>(
      'gradeAllAbsentStudents',
    );
    return (await fn(data)).data;
  },
  grade: async (data: {
    tenantId: string;
    submissionId: string;
    rawPoints: number;
    feedback?: string;
    formativeBreakdown?: FormativeBreakdownDto;
    rubricAssessment?: RubricAssessmentEntryDto[];
    reviewId?: string;
  }): Promise<{ finalPoints: number; latePenalty: number }> => {
    const fn = callable<typeof data, { finalPoints: number; latePenalty: number }>(
      'gradeSubmission',
    );
    return (await fn(data)).data;
  },
  saveAnnotations: async (data: {
    tenantId: string;
    submissionId: string;
    annotations: PdfAnnotationDto[];
    dismissedIssueKeys?: string[];
  }): Promise<{ count: number }> => {
    const fn = callable<typeof data, { count: number }>('saveSubmissionAnnotations');
    return (await fn(data)).data;
  },

  // Assisted Review (AI)
  getAssignmentRubric: async (data: {
    tenantId: string;
    assignmentId: string;
  }): Promise<{ criteria: RubricCriterionDto[]; config: ReviewConfigDto | null }> => {
    const fn = callable<
      typeof data,
      { criteria: RubricCriterionDto[]; config: ReviewConfigDto | null }
    >('getAssignmentRubric');
    return (await fn(data)).data;
  },
  generateReview: async (data: {
    tenantId: string;
    submissionId: string;
    force?: boolean;
    criterionIds?: string[];
    instructions?: string;
  }): Promise<{ review: AssistedReviewDto; rubricCriteria: RubricCriterionDto[] }> => {
    // The model call (incl. PDF/image vision) can take well over the 70s default
    // callable timeout. Server budget is 300s; give the client just under that so
    // it doesn't give up while the backend is still working.
    const fn = callable<
      typeof data,
      { review: AssistedReviewDto; rubricCriteria: RubricCriterionDto[] }
    >('generateAssistedReview', { timeout: 290_000 });
    return (await fn(data)).data;
  },
  getReviews: async (data: {
    tenantId: string;
    submissionId: string;
  }): Promise<{ reviews: AssistedReviewDto[] }> => {
    const fn = callable<typeof data, { reviews: AssistedReviewDto[] }>('getAssistedReviews');
    return (await fn(data)).data;
  },
  returnForRevision: async (data: {
    tenantId: string;
    submissionId: string;
    note?: string;
  }): Promise<{ status: string }> => {
    const fn = callable<typeof data, { status: string }>('returnSubmissionForRevision');
    return (await fn(data)).data;
  },
  reopenSubmission: async (data: {
    tenantId: string;
    submissionId: string;
  }): Promise<{ attempt: number }> => {
    const fn = callable<typeof data, { attempt: number }>('reopenSubmission');
    return (await fn(data)).data;
  },
  get: async (data: {
    tenantId: string;
    submissionId: string;
    requestingStudentId?: string;
  }): Promise<SubmissionDto> => {
    const fn = callable<typeof data, SubmissionDto>('getSubmission');
    return (await fn(data)).data;
  },
  getFileUrl: async (data: {
    tenantId: string;
    submissionId: string;
    storagePath: string;
    requestingStudentId?: string;
  }): Promise<{ url: string; expiresInSeconds: number }> => {
    const fn = callable<typeof data, { url: string; expiresInSeconds: number }>(
      'getSubmissionFileUrl',
    );
    return (await fn(data)).data;
  },
};
